/**
 * Pickett plot — yang bisa digeser, dan yang bisa dipersempit.
 *
 * Yang berdiri di sini sebelumnya adalah gambar: awan Rt–PHIE pada sumbu
 * log–log dengan satu garis Sw = 1 yang digambar dari parameter proyek. Gambar
 * itu benar dan tidak bisa dipakai, sebab satu-satunya hal yang dikerjakan
 * orang pada plot Pickett adalah *menggeser garisnya sampai ia duduk di kaki
 * kiri awan*, lalu membaca Rw dan m dari kedudukan garis itu. Sebuah garis yang
 * hanya bisa diubah dengan membuka layar parameter, menebak angka, menyimpan,
 * dan kembali ke sini untuk melihat hasilnya bukan garis yang bisa dicocokkan —
 * ia gambar dari keputusan yang sudah diambil di tempat lain.
 *
 * Karena itu tiga hal:
 *
 * - **Garisnya digeser dengan tangan.** Menyeret badannya menggeser Rw dengan
 *   m tetap; menyeret salah satu ujungnya memutar garis terhadap ujung yang
 *   lain, dan itulah m. Keduanya membaca Archie ke arah sebaliknya —
 *   Rt = a·Rw/(φ^m·Sw^n) — jadi yang berubah adalah angka yang dipakai
 *   perhitungan, bukan sekadar letak garis di layar.
 * - **Garis pendukungnya ikut.** Sw = 0,8 sampai 0,2 berdiri di kanan garis
 *   air, sebab pertanyaannya bukan "di mana Sw = 1" melainkan "berapa Sw titik
 *   yang ini". Ditambah satu garis regresi awan itu sendiri: kemiringannya
 *   adalah −1/m dan potongannya membawa Rw, dan tombol di sebelahnya
 *   memindahkan garis Archie ke atasnya.
 * - **Lingkupnya bisa dipersempit sampai ke meternya.** Sumur, selang marker,
 *   lalu batas MD yang digeser. Rw sebuah lapisan bukan Rw lapangan, dan awan
 *   yang memuat tiga zona sekaligus tidak punya satu kaki kiri untuk dicocoki.
 */

import { useMemo, useRef, useState } from "react";

import { api } from "../api";
import {
  DEFAULT_SCOPE,
  normalizeScope,
  scopeParams,
  StackedDropdown,
  wellModeOf,
} from "./DataScope";
import { LogSource } from "./LogSource";
import { adaptOne, useAdaptOnNewScope, usePreferredCurve } from "../lib/curveoptions";
import { logTicks, tickDecimals } from "../lib/ticks";
import { distinctColors } from "../lib/xplot";
import type { FieldCrossplot, Well } from "../types";
import { Button, Chip, Empty, Loading, Notice, NumberField, Panel } from "./ui";
import { useAsync, useDebounced, useElementSize, useStored } from "../hooks";

/** Konstanta Archie yang sedang dipakai garisnya. */
export interface Archie {
  a: number;
  m: number;
  n: number;
  rw: number;
}

/**
 * Kurva resistivitas — satu-satunya yang pantas mengisi sumbu mendatar Pickett.
 *
 * Daftar dan bukan tebakan awalan: "R" di depan juga milik RHOB, dan sebuah
 * densitas yang tersasar ke sumbu resistivitas logaritmik menghasilkan plot
 * yang tetap tergambar rapi tanpa berarti apa pun. Bila tidak satu pun dari
 * daftar ini ada di lapangannya, yang ditawarkan seluruh kurvanya — menolak
 * memberi pilihan sama sekali lebih buruk daripada memberi pilihan yang salah
 * satunya keliru.
 */
const RESISTIVITY = new Set([
  "RT", "RXO", "RD", "RS", "RESD", "RESM", "RESS", "ILD", "ILM", "SFL",
  "LLD", "LLS", "MSFL", "RLA1", "RLA2", "RLA3", "RLA4", "RLA5",
  "AT10", "AT20", "AT30", "AT60", "AT90",
  // AIT Schlumberger dan induksi/laterolog lain yang dibawa LAS Teapot Dome.
  "AHT10", "AHT20", "AHT30", "AHT60", "AHT90", "AHF90", "HLLD", "HLLS", "RILD", "RILM",
  "RDEP", "RMED", "RSHA",
]);

/** Kurva porositas, penghuni sumbu tegaknya. */
const POROSITY = new Set(["PHIE", "PHIT", "PHI", "PIGN", "PHID", "NPHI", "PHIS",
                          "DPHI", "DPHZ", "SPHI", "TNPH"]);

/** Garis Sw yang tergambar sebagai pendukung, dari yang paling basah. */
const SW_LINES = [1, 0.8, 0.6, 0.4, 0.2];

const PAD = 3;
const SPAN = 100 - PAD * 2;

/** Tepi kotak gambar dalam satuan viewBox. */
const BOX = { lo: PAD, hi: PAD + SPAN };

/**
 * Ruas yang benar-benar tampak di dalam kotak — atau `null` bila tak sedikit pun.
 *
 * Garis Sw digambar dari ujung sumbu porositas ke ujung yang lain, dan Rt pada
 * kedua ujung itu kerap jatuh di luar dekade sumbu mendatar. Yang tergambar
 * memang terpotong sendiri oleh tepi SVG, jadi kesalahannya tak terlihat pada
 * garisnya; yang tidak ikut terpotong adalah **pegangannya**. Pegangan yang
 * digambar pada cx −47 atau 123 duduk di luar kanvas: ia tidak terlihat, tidak
 * bisa ditekan, dan pada KTI-22 kedua-duanya di luar sekaligus — memutar garis
 * dengan tangan menjadi mustahil justru di sumur yang paling perlu diputar,
 * tanpa satu pun tanda bahwa ada yang hilang.
 *
 * Liang–Barsky: ruasnya dipotong sebagai selang parameter t, jadi urutan
 * ujungnya terjaga — yang dipulangkan sebagai `a` tetap ujung yang berangkat
 * dari `a`, dan pegangan atas tidak pernah bertukar tempat dengan yang bawah.
 */
export function clipToBox(
  a: { x: number; y: number },
  b: { x: number; y: number },
  box: { lo: number; hi: number } = BOX,
): { a: { x: number; y: number }; b: { x: number; y: number } } | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  const edges: [number, number][] = [
    [-dx, a.x - box.lo],
    [dx, box.hi - a.x],
    [-dy, a.y - box.lo],
    [dy, box.hi - a.y],
  ];
  for (const [p, q] of edges) {
    // Sejajar dengan tepi ini: entah seluruhnya di dalam, entah seluruhnya di
    // luar — dan yang di luar tidak punya ruas yang tampak sama sekali.
    if (p === 0) {
      if (q < 0) return null;
      continue;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return null;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return null;
      if (r < t1) t1 = r;
    }
  }
  // Ujung yang tidak terpotong dipulangkan apa adanya: −47,1 + 97,1 tidak
  // persis 50, dan sebuah pegangan yang bergeser sepersepuluh ribu piksel
  // karena ujung *seberangnya* dipotong adalah pergeseran tanpa sebab.
  const at = (t: number) =>
    t === 0 ? { x: a.x, y: a.y }
    : t === 1 ? { x: b.x, y: b.y }
    : { x: a.x + t * dx, y: a.y + t * dy };
  return { a: at(t0), b: at(t1) };
}

/** Batas kewarasan. Di luar ini garisnya bukan lagi Archie melainkan salah seret. */
const M_RANGE: [number, number] = [0.5, 4];
const RW_RANGE: [number, number] = [1e-4, 100];

/** Jari-jari titik dan pegangan, dalam piksel layar. */
const DOT = 2.2;
const HANDLE = 4;

/**
 * Jari-jari sebuah titik bulat di dalam kotak yang koordinatnya diregangkan.
 *
 * Kotak ini menggambar pada viewBox 100x100 dengan `preserveAspectRatio="none"`,
 * sebab garis Archie harus berakhir tepat di sudut kotaknya berapa pun bentuk
 * kotak itu. Harga yang ikut terbayar diam-diam adalah lingkaran: satu satuan
 * mendatar bukan satu satuan tegak, jadi `r` yang sama melahirkan elips —
 * pada layar penuh selebar seribu piksel, titiknya menjadi lonjong dua banding
 * satu dan awannya terbaca lebih rapat daripada yang sebenarnya. Karena itu
 * jari-jarinya dihitung kembali ke satuan viewBox pada tiap sumbu: yang
 * tergambar adalah elips yang tampil bulat.
 *
 * Kotak yang belum terukur — render pertama, dan jsdom — memulangkan satuan
 * yang sama pada kedua sumbu; itu bukan bulat, tapi juga tidak hilang.
 */
export function dotRadii(
  size: { width: number; height: number },
  px: number,
): { rx: number; ry: number } {
  if (!(size.width > 0) || !(size.height > 0)) return { rx: px / 2, ry: px / 2 };
  return { rx: (px * 100) / size.width, ry: (px * 100) / size.height };
}

/**
 * Kalimat untuk plot yang kosong.
 *
 * "Tidak ada sampel pada selang ini" menyalahkan selangnya, dan itu keliru
 * setiap kali sebab yang sebenarnya adalah kurva yang tidak ada di sumur itu:
 * yang membacanya lalu menggeser-geser batas kedalaman yang sudah benar,
 * mencari sampel yang tidak akan pernah muncul di sana. Server sudah menyebut
 * alasannya sumur demi sumur — tinggal dikatakan.
 */
export function emptyReason(skipped: { well: string; reason: string }[]): string {
  if (!skipped.length) return "No sample in this interval.";
  if (skipped.length === 1) return `${skipped[0].well} skipped — ${skipped[0].reason}.`;
  const reasons = new Set(skipped.map((row) => row.reason));
  return reasons.size === 1
    ? `${skipped.length} wells skipped — ${skipped[0].reason}.`
    : `${skipped.length} wells skipped, each for its own reason; the list is below.`;
}

/**
 * Tempat label sebuah garis Sw berdiri, dalam persen kotaknya.
 *
 * Label ditulis di ujung atas ruas yang tampak, dan sebelumnya garis yang
 * ujungnya jatuh di luar kotak kehilangan labelnya sama sekali — disaring
 * keluar supaya tulisannya tidak menjorok. Yang hilang justru "Sw 1,0", sebab
 * garis airlah yang paling sering keluar lewat tepi: pada KTI-22 dan pada
 * selang marker KTI-17 yang tersisa di layar hanya "0,2", dan sebuah plot
 * Pickett yang garis airnya tidak bernama adalah plot yang menyuruh
 * pembacanya menghitung sendiri garis mana yang mana.
 *
 * Jadi labelnya tidak dibuang melainkan dipindahkan: di sepersepuluh kanan
 * kotak ia ditulis di kiri titiknya, dan di dekat kaki kotak ia ditahan supaya
 * tidak jatuh di luar. Yang bergeser tulisannya; garis yang ditunjuknya tetap.
 */
export function labelSpot(a: { x: number; y: number }): {
  left: number;
  top: number;
  flip: boolean;
} {
  return { left: a.x, top: Math.min(Math.max(a.y, 1), 92), flip: a.x > 88 };
}

interface LogAxis {
  /** Ujung dalam log10, sebab seluruh plot ini hidup di sana. */
  lo: number;
  hi: number;
  ticks: number[];
}

function clamp(value: number, [lo, hi]: [number, number]): number {
  return Math.min(hi, Math.max(lo, value));
}

/**
 * Angka sebagaimana pantas dilaporkan sebuah seretan.
 *
 * Sebuah garis yang digeser dengan tangan tidak membawa tujuh belas angka di
 * belakang koma, dan kotak isian yang memuat 0,10768590887814272 mengaku tahu
 * apa yang tidak diketahui siapa pun. Empat angka berarti — dan bukan empat
 * desimal — supaya Rw 0,0086 tidak dibulatkan menjadi 0,0086 sementara Rw
 * 0,86 tidak kehilangan apa pun.
 */
function tidy(value: number): number {
  return Number(value.toPrecision(4));
}

/**
 * Sumbu logaritmik yang berhenti di batas dekade.
 *
 * Ujung yang mengikuti data persis membuat sumbu berpindah tiap kali
 * penyaringnya berubah, dan plot Pickett dibaca dengan mata: garis yang
 * kemiringannya sama terlihat berbeda begitu sumbunya sendiri ikut bergerak.
 */
function decadeAxis(values: number[], limit: [number, number]): LogAxis {
  const positive = values.filter((v) => Number.isFinite(v) && v > 0);
  const lo = positive.length ? Math.floor(Math.log10(Math.min(...positive))) : limit[0];
  const hi = positive.length ? Math.ceil(Math.log10(Math.max(...positive))) : limit[1];
  // Dijepit ke dekade yang memang dibaca orang. Satu sampel PHIE 0,0002 —
  // sebuah nol yang lolos dari perhitungan — memanjangkan sumbu porositas
  // menjadi empat dekade, dan seluruh awan yang sebenarnya lalu berdesak di
  // seperempat teratas kotaknya. Yang di luar jepitan tidak digambar dan tidak
  // pula dijepit ke tepi: ia hilang, sebagaimana titik di luar skala.
  const low = Math.max(Math.min(lo, limit[1] - 1), limit[0]);
  const high = Math.min(Math.max(hi, low + 1), limit[1]);
  return { lo: low, hi: high > low ? high : low + 1, ticks: [] };
}

function withTicks(axis: LogAxis): LogAxis {
  return { ...axis, ticks: logTicks(10 ** axis.lo, 10 ** axis.hi) };
}

/** Kedudukan sebuah nilai pada sumbunya, 0 di pangkal dan 1 di ujung. */
function at(axis: LogAxis, value: number): number {
  return (Math.log10(value) - axis.lo) / (axis.hi - axis.lo || 1);
}

function atLog(axis: LogAxis, logValue: number): number {
  return (logValue - axis.lo) / (axis.hi - axis.lo || 1);
}

/**
 * Rt sebuah porositas pada satu garis Sw, menurut Archie.
 *
 * Dikembalikan dalam log10 karena di situlah garis ini lurus, dan lurus
 * adalah satu-satunya alasan plot ini digambar log–log.
 */
export function logRtOf(archie: Archie, logPhi: number, sw: number): number {
  return Math.log10(archie.a * archie.rw) - archie.m * logPhi - archie.n * Math.log10(sw);
}

/**
 * Menggeser garis air tanpa memutarnya: m tetap, Rw yang berpindah.
 *
 * Inilah seretan yang paling sering dipakai — awan yang bentuknya sudah benar
 * tetapi duduk terlalu ke kiri adalah salah Rw dan bukan salah m — dan karena
 * itu ia harus benar-benar hanya mengubah satu angka.
 */
export function dragBody(archie: Archie, logRt: number, logPhi: number): Archie {
  return { ...archie, rw: tidy(clamp(10 ** (logRt + archie.m * logPhi) / (archie.a || 1), RW_RANGE)) };
}

/**
 * Memutar garis air terhadap ujungnya yang lain.
 *
 * Rw ikut dihitung ulang justru supaya poros tetap di tempatnya: tanpa itu,
 * memutar juga menggeser, dan tidak ada satu pun seretan yang mengubah hanya
 * satu angka. Kursor yang sejajar dengan porosnya tidak menghasilkan
 * kemiringan apa pun, dan yang dipulangkan adalah Archie yang sama.
 */
export function dragEnd(
  archie: Archie,
  pivotLogPhi: number,
  cursor: { logRt: number; logPhi: number },
): Archie {
  const pivotLogRt = logRtOf(archie, pivotLogPhi, 1);
  const dPhi = cursor.logPhi - pivotLogPhi;
  if (Math.abs(dPhi) < 1e-3) return archie;
  const m = tidy(clamp(-(cursor.logRt - pivotLogRt) / dPhi, M_RANGE));
  const rw = tidy(clamp(10 ** (pivotLogRt + m * pivotLogPhi) / (archie.a || 1), RW_RANGE));
  return { ...archie, m, rw };
}

/**
 * Regresi awan itu sendiri, dibaca sebagai sepasang angka Archie.
 *
 * Server memasangnya pada nilai yang *digambar* — di sini log10 keduanya — jadi
 * kemiringannya adalah d(log φ)/d(log Rt), dan garis Sw = 1 punya kemiringan
 * −1/m. Kemiringan yang tidak menurun bukan garis air melainkan awan tanpa
 * tren, dan menamainya m akan melaporkan angka yang tidak berarti apa-apa;
 * karena itu yang dipulangkan null, bukan angka yang kebetulan ada.
 */
export function archieFromFit(
  slope: number,
  intercept: number,
  a: number,
): { m: number; rw: number } | null {
  if (!(slope < 0)) return null;
  const m = -1 / slope;
  return { m: tidy(m), rw: tidy(10 ** (intercept * m) / (a || 1)) };
}

/**
 * R² serendah-rendahnya yang masih boleh disebut tren.
 *
 * Sengaja rendah, dan itu bukan kelonggaran melainkan fisika: awan Pickett
 * yang bagus justru *tidak* rapat. Titik pada selang berhidrokarbon duduk di
 * atas garis air karena memang di situ tempatnya, jadi R² 0,3 pada sumur
 * berisi minyak adalah pemandangan biasa, dan menuntut 0,7 di sini berarti
 * menolak justru sumur-sumur yang plot ini dibuat untuknya. Yang hendak
 * ditolak ambang ini hanya satu: awan yang kemiringannya tak dapat dibedakan
 * dari nol, tempat kemiringan yang terbaca adalah derau dan m yang lahir
 * darinya adalah kebetulan.
 */
const R2_FLOOR = 0.1;

/**
 * Alasan sebuah regresi tidak boleh dibaca sebagai Archie — atau `null` bila boleh.
 *
 * Penjaga di `archieFromFit` hanya menolak awan yang trennya menaik, dan itu
 * membiarkan lewat hal yang lebih buruk daripada penolakan: KTI-09 dengan R²
 * 0,001 melaporkan "m 130,40 · Rw 0,0000" dan tetap menawarkan tombol
 * **pakai** — yang kemudian menjepit angka itu ke m 4 / Rw 0,0001. Yang
 * dipulangkan bukan hasil cocokan melainkan batas kewarasan penjepitnya
 * sendiri, dan tidak ada apa pun di layar yang membedakan keduanya. Sebuah
 * tombol yang menawarkan angka yang tidak berarti apa-apa lebih buruk
 * daripada tombol yang tidak ada, sebab yang menekannya berhak menganggap
 * angka yang ditawarkan aplikasi sudah diperiksa aplikasi.
 *
 * Karena itu: kalimatnya pendek, sebab ia ditulis di dalam kotak selebar
 * seruas jari; dan ia menyebut angkanya, sebab "regresi ditolak" tanpa
 * angka adalah penolakan yang tidak bisa diperiksa siapa pun.
 */
export function fitRefusal(
  read: { m: number; rw: number } | null,
  r2: number | null,
): string | null {
  if (!read) return "a cloud with no downward trend";
  if (r2 === null) return "R² not measurable";
  if (r2 < R2_FLOOR) return `R² ${fmt(r2, 3)} — scatter, not a trend`;
  if (read.m < M_RANGE[0] || read.m > M_RANGE[1])
    return `m ${fmt(read.m, 2)} outside ${fmt(M_RANGE[0], 1)}–${fmt(M_RANGE[1], 1)}`;
  if (read.rw < RW_RANGE[0] || read.rw > RW_RANGE[1])
    return `Rw ${fmt(read.rw, 4)} outside the formation-water bounds`;
  return null;
}

/** Angka sebagaimana kotak keterangan ini menuliskan angka. */
function fmt(value: number, digits: number): string {
  return value.toFixed(digits);
}

/**
 * Kunci simpanan garis yang sudah dicocokkan — satu per sumur.
 *
 * `null` adalah pilihan "ikut lingkup": ia bukan sebuah sumur, jadi ia
 * mendapat kuncinya sendiri dan tidak menumpang pada sumur mana pun.
 */
export function archieKey(projectId: number, wellId: number | null): string {
  return `evolog.pickett.archie.${projectId}.${wellId === null ? "lingkup" : wellId}`;
}

export function PickettPanel({
  project,
  wells,
  available,
  dataVersion = 0,
  expanded = false,
  onToggleExpand,
  activeWellId = null,
}: {
  project: { id: number };
  wells: Well[];
  available: string[];
  dataVersion?: number;
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** Sumur aktif di cangkang — hanya bawaan saat panel ini pertama dibuka. */
  activeWellId?: number | null;
}) {
  // Sumber log Pickett sendiri, terpisah dari crossplot: sumur, radius, dan
  // selang kedalamannya dipilih di sini dan disimpan di sini. Dibuka pada
  // sumur aktif, sebab garis Archie dicocokkan per sumur.
  const [storedScope, setScope] = useStored<unknown>(`evolog.pickett.source.${project.id}`, {
    ...DEFAULT_SCOPE,
    // Tanpa sumur aktif tidak ada sumur yang bisa dibuka sendirian: mulai dari
    // seluruh lapangan, dan "Tunggal" satu klik dari sana.
    wellMode: activeWellId === null ? "multi" : "single",
    depthMode: "all",
    wells: activeWellId === null ? [] : [activeWellId],
  });
  const scope = useMemo(() => normalizeScope(storedScope), [storedScope]);
  // Mandiri dari crossplot dan dari pemilih WELL di cangkang: sumur Pickett
  // hanya berpindah dari panel ini. Dulu keduanya saling menyeret — memilih
  // sumur di sini memindahkan pemilih di cangkang, dan pemilih itu lalu
  // memindahkan crossplot di sebelahnya.

  const rt = usePreferredCurve(useStored("evolog.xplot.rt", "RT"));
  const phi = usePreferredCurve(useStored("evolog.pickett.phi", "PHIE"));
  const [rtCurve, setRtCurve] = [rt.value, rt.choose];
  const [phiCurve, setPhiCurve] = [phi.value, phi.choose];
  const [showSupport, setShowSupport] = useStored("evolog.pickett.support", true);
  const [showFit, setShowFit] = useStored("evolog.pickett.fit", true);

  // Isian MD yang diketik melahirkan satu permintaan tiap ketukan kalau
  // dibiarkan; yang ditunggu kuerinya, bukan gambarnya.
  const settled = useDebounced(scope, 260);
  const settledWell = wellModeOf(settled) === "single" ? settled.wells[0] ?? null : null;

  // Garis yang sudah dicocokkan menempel pada sumurnya, dan itulah sebabnya
  // kuncinya memuat sumur itu. Satu kunci per proyek membawa Rw hasil cocokan
  // di sumur ini ke awan sumur berikutnya tanpa sepatah kata pun. Lingkup
  // lebih dari satu sumur berbagi satu kunci "lingkup".
  const [tuned, setTuned] = useStored<Archie | null>(archieKey(project.id, settledWell), null);

  const payload = useAsync(
    (signal) =>
      api.get<FieldCrossplot>(
        `/api/projects/${project.id}/crossplot/`,
        {
          x: rtCurve,
          y: phiCurve,
          log_x: "1",
          log_y: "1",
          // Penyaring Sw tidak dipakai: Pickett justru membaca Sw dari awannya.
          ...scopeParams({ ...settled, swMin: 0 }),
        },
        signal,
      ),
    [project.id, rtCurve, phiCurve, settled, dataVersion],
  );

  // Resistivitas dan porositas mengikuti sumur, seperti sumbu crossplot: sumur
  // yang belum dievaluasi tidak punya PHIE, tetapi hampir selalu punya NPHI.
  useAdaptOnNewScope(payload.data, payload.loading, JSON.stringify(settled), () => {
    const here = payload.data?.available ?? [];
    rt.use(adaptOne(here.filter((n) => RESISTIVITY.has(n.toUpperCase())), rt.preferred,
                    ["RT", "ILD", "LLD", "AHT90", "HLLD", "RILD", "RD"]) ?? rt.preferred);
    phi.use(adaptOne(here.filter((n) => POROSITY.has(n.toUpperCase())), phi.preferred,
                     ["PHIE", "PHIT", "NPHI", "DPHI", "DPHZ", "TNPH"]) ?? phi.preferred);
  });

  const constants = payload.data?.constants;
  const archie: Archie = tuned ?? {
    a: constants?.a ?? 1,
    m: constants?.m ?? 2,
    n: constants?.n ?? 2,
    rw: constants?.rw ?? 0.05,
  };
  const set = (patch: Partial<Archie>) => setTuned({ ...archie, ...patch });

  const asOptions = (names: string[], fallback: string) =>
    (names.length ? names : [fallback]).map((name) => ({ value: name, label: name }));
  const rtOptions = asOptions(
    available.filter((name) => RESISTIVITY.has(name.toUpperCase())).length
      ? available.filter((name) => RESISTIVITY.has(name.toUpperCase()))
      : available,
    rtCurve,
  );
  const phiOptions = asOptions(
    available.filter((name) => POROSITY.has(name.toUpperCase())).length
      ? available.filter((name) => POROSITY.has(name.toUpperCase()))
      : available,
    phiCurve,
  );

  const controls = (
    <LogSource
      title="Sumber log — Pickett"
      wells={wells}
      scope={scope}
      onChange={setScope}
      activeWellId={activeWellId}
      screening={payload.data?.screening}
      rockFilter
      curves={
        <div style={{ display: "flex", gap: 8 }}>
          <StackedDropdown label="RESISTIVITAS" value={rtCurve} options={rtOptions} onChange={setRtCurve} />
          <StackedDropdown label="POROSITY" value={phiCurve} options={phiOptions} onChange={setPhiCurve} />
        </div>
      }
    />
  );

  const params = (
    <>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <NumberField label="Rw" unit="Ω·m" value={archie.rw} step={0.001} min={0.0001}
                       onChange={(rw) => set({ rw: clamp(rw, RW_RANGE) })} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <NumberField label="m" value={archie.m} step={0.01} min={M_RANGE[0]} max={M_RANGE[1]}
                       onChange={(m) => set({ m: clamp(m, M_RANGE) })} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <NumberField label="a" value={archie.a} step={0.01} min={0.1}
                       onChange={(a) => set({ a: Math.max(0.1, a) })} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <NumberField label="n" value={archie.n} step={0.01} min={0.5}
                       onChange={(n) => set({ n: Math.max(0.5, n) })} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Chip active={showSupport} onClick={() => setShowSupport(!showSupport)}>
          Supporting Sw lines
        </Chip>
        <Chip active={showFit} onClick={() => setShowFit(!showFit)}>
          Cloud regression
        </Chip>
        <Chip active={tuned === null} onClick={() => setTuned(null)}>
          Back to the project parameters
        </Chip>
      </div>
      {tuned !== null && constants && (
        <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
          Moved off the project parameters — Rw {constants.rw}, m {constants.m}. These numbers
          belong to {settledWell === null ? "this plot's source" : wells.find((w) => w.id === settledWell)?.name ?? "this well"}{" "}
          alone, and every other well starts again from the project parameters. What holds for
          the whole field is saved to a parameter set from the Analysis screen.
        </span>
      )}
    </>
  );

  return (
    // `flex: none`: panel ini setinggi isinya, dan kolomnya yang bergulir.
    // Sebagai anak flex yang bisa menyusut ia dipepatkan sampai isinya
    // melimpah keluar — chip terakhirnya lalu duduk di atas judul histogram di
    // bawahnya, tanpa satu pun batang gulir yang menawarkan jalan keluar.
    <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: "none", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
        <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>Pickett</span>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {payload.data ? `${payload.data.wells.length} wells · ${payload.data.points.count.toLocaleString("en-US")} points` : ""}
        </span>
        <span style={{ flex: 1 }} />
        {onToggleExpand && (
          <Button
            variant="ghost"
            onClick={onToggleExpand}
            style={{ height: 22, font: "500 10px var(--font-sans)" }}
          >
            {expanded ? "Close" : "Full screen"}
          </Button>
        )}
      </div>

      {payload.loading && !payload.data && <Loading />}
      {payload.error && <Notice tone="error">{payload.error}</Notice>}
      {payload.data && (
        <PickettCanvas
          payload={payload.data}
          archie={archie}
          onArchie={(next) => setTuned(next)}
          showSupport={showSupport}
          showFit={showFit}
          rtLabel={rtCurve}
          phiLabel={phiCurve}
          // Tinggi yang pasti, juga di kolom sempit: di dalam kolom yang
          // bergulir tidak ada ruang sisa untuk dibagi, dan kotak ber-`flex: 1`
          // tanpa tinggi yang pasti mengerut sampai angka sumbunya menindih
          // kendali di bawahnya.
          height={expanded ? 460 : 300}
        />
      )}
      {payload.data && !payload.data.points.count && (
        <Empty>{emptyReason(payload.data.skipped)}</Empty>
      )}

      {/* Grid dan bukan flex column. Kendali yang ditumpuk sebagai anak flex
          menyusut ketika ruangnya kurang — dan yang pertama menyusut adalah
          yang paling pendek, sehingga pemilih "Seluruh sumur / Selang marker"
          tinggal segaris tipis yang tidak bisa ditekan. Baris grid berukuran
          isinya, dan yang kelebihan digulir. */}
      {expanded ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px", alignContent: "start", flex: "none" }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10, alignContent: "start" }}>
            {controls}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10, alignContent: "start" }}>
            {params}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 10,
            alignContent: "start",
            flex: "none",
          }}
        >
          {controls}
          {params}
        </div>
      )}

      {payload.data?.skipped.length ? (
        <Panel title={`Skipped — ${payload.data.skipped.length} wells`}>
          {payload.data.skipped.map((row) => (
            <span
              key={`${row.well}-${row.reason}`}
              style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}
            >
              {row.well} — {row.reason}
            </span>
          ))}
        </Panel>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------- Kanvas */

type DragKind = "body" | "top" | "bottom";

/**
 * Awan Pickett berikut garis-garisnya, dan tempat garis itu diseret.
 *
 * Seluruh geometri hidup dalam log10: sumbu, garis Archie, garis regresi, dan
 * kedudukan kursor. Yang dibaca dari layar diubah kembali menjadi Rw dan m
 * lewat Archie yang sama dengan yang dipakai mesin perhitungan — bukan lewat
 * rumus kedua yang kebetulan menghasilkan garis yang mirip.
 */
function PickettCanvas({
  payload,
  archie,
  onArchie,
  showSupport,
  showFit,
  rtLabel,
  phiLabel,
  height,
}: {
  payload: FieldCrossplot;
  archie: Archie;
  onArchie: (next: Archie) => void;
  showSupport: boolean;
  showFit: boolean;
  rtLabel: string;
  phiLabel: string;
  height?: number;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const [sizeRef, size] = useElementSize<HTMLDivElement>();
  const [drag, setDrag] = useState<DragKind | null>(null);

  // Titik dan pegangan digambar sebagai elips yang tampil bulat; garis dan
  // kisinya tetap teregang, sebab justru itu yang membuat garis Archie
  // berakhir tepat di sudut kotaknya.
  const dot = useMemo(() => dotRadii(size, DOT), [size]);
  const grip = useMemo(() => dotRadii(size, HANDLE), [size]);

  const axes = useMemo(() => {
    // Rt 0,1–10.000 Ω·m dan φ 0,01–1 v/v: rentang yang tercetak pada tiap
    // chart Pickett di mana pun ia tercetak.
    const x = withTicks(decadeAxis(payload.points.x, [-1, 4]));
    const y = withTicks(decadeAxis(payload.points.y, [-2, 0]));
    return { x, y };
  }, [payload.points.x, payload.points.y]);

  const wellColors = useMemo(
    () => distinctColors(payload.wells.map((row) => row.color)),
    [payload.wells],
  );

  const points = useMemo(() => {
    const { x, y, well } = payload.points;
    const out: { cx: number; cy: number; fill: string }[] = [];
    for (let i = 0; i < x.length; i++) {
      if (!(x[i] > 0) || !(y[i] > 0)) continue;
      const tx = at(axes.x, x[i]);
      const ty = at(axes.y, y[i]);
      if (tx < 0 || tx > 1 || ty < 0 || ty > 1) continue;
      out.push({
        cx: PAD + tx * SPAN,
        cy: PAD + (1 - ty) * SPAN,
        fill: wellColors[well[i]] ?? "#8b939e",
      });
    }
    return out;
  }, [payload.points, axes, wellColors]);

  /** Garis Sw sebagai ruas dari ujung atas ke ujung bawah sumbu porositas. */
  const swLines = useMemo(() => {
    const list = showSupport ? SW_LINES : [1];
    return list.flatMap((sw) => {
      const top = { logPhi: axes.y.hi, logRt: logRtOf(archie, axes.y.hi, sw) };
      const bottom = { logPhi: axes.y.lo, logRt: logRtOf(archie, axes.y.lo, sw) };
      const point = (p: { logPhi: number; logRt: number }) => ({
        x: PAD + atLog(axes.x, p.logRt) * SPAN,
        y: PAD + (1 - atLog(axes.y, p.logPhi)) * SPAN,
      });
      // Ujung ruas yang tampak, bukan ujung sumbu: di situlah pegangannya
      // diletakkan, dan di situ pula label garisnya berdiri.
      const seg = clipToBox(point(top), point(bottom));
      if (!seg) return [];
      return [{ sw, a: seg.a, b: seg.b, d: `M${seg.a.x} ${seg.a.y} L${seg.b.x} ${seg.b.y}` }];
    });
  }, [archie, axes, showSupport]);

  // Bisa tidak ada sama sekali: sebuah garis air yang seluruhnya di luar kotak
  // tidak punya ujung yang tampak untuk dipegang. Yang memulangkannya ke dalam
  // kotak adalah tombol "kembali ke parameter proyek", bukan seretan.
  const water = swLines.find((line) => line.sw === 1);

  /**
   * Regresi awan itu sendiri, dibaca sebagai Archie.
   *
   * Server memasangnya pada nilai yang *digambar* — di sini log10 keduanya —
   * jadi kemiringannya adalah d(log φ)/d(log Rt), dan garis Sw = 1 punya
   * kemiringan −1/m. Kemiringan yang tidak menurun bukan garis air melainkan
   * awan tanpa tren, dan menamainya m akan melaporkan angka yang tidak berarti.
   */
  const fit = useMemo(() => {
    const regression = payload.regression;
    if (!regression) return null;
    const read = archieFromFit(regression.slope, regression.intercept, archie.a);
    // Garisnya tetap digambar meski angkanya ditolak: regresi itu sendiri
    // adalah keterangan yang benar tentang awannya, dan menghilangkannya
    // diam-diam membuat yang menyalakan "regresi" mengira sakelarnya rusak.
    // Yang ditahan hanya penamaannya sebagai m dan Rw.
    const point = (logRt: number) => ({
      x: PAD + atLog(axes.x, logRt) * SPAN,
      y: PAD + (1 - atLog(axes.y, regression.slope * logRt + regression.intercept)) * SPAN,
    });
    const a = point(Math.max(regression.x0, axes.x.lo));
    const b = point(Math.min(regression.x1, axes.x.hi));
    return {
      read,
      refusal: fitRefusal(read, regression.r2),
      r2: regression.r2,
      n: regression.n,
      d: `M${a.x} ${a.y} L${b.x} ${b.y}`,
    };
  }, [payload.regression, axes, archie.a]);

  /** Porositas (log10) di sebuah ketinggian layar — kebalikan penggambarannya. */
  const logPhiAtY = (y: number) =>
    axes.y.lo + (1 - (y - PAD) / SPAN) * (axes.y.hi - axes.y.lo);

  /** Kedudukan kursor sebagai sepasang nilai log10 pada sumbunya. */
  const readPointer = (event: React.PointerEvent): { logRt: number; logPhi: number } | null => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    const fx = ((event.clientX - rect.left) / rect.width) * 100;
    const fy = ((event.clientY - rect.top) / rect.height) * 100;
    return {
      logRt: axes.x.lo + ((fx - PAD) / SPAN) * (axes.x.hi - axes.x.lo),
      logPhi: axes.y.lo + ((1 - (fy - PAD) / SPAN) / 1) * (axes.y.hi - axes.y.lo),
    };
  };

  const onMove = (event: React.PointerEvent) => {
    if (!drag) return;
    const cursor = readPointer(event);
    if (!cursor) return;
    if (drag === "body") {
      onArchie(dragBody(archie, cursor.logRt, cursor.logPhi));
      return;
    }
    // Poros seretan ujung adalah ujung yang satunya lagi — ujung *yang tampak*,
    // yaitu pegangan yang satu lagi. Memutar terhadap ujung sumbu akan
    // menggeser pegangan yang justru sedang dilihat orang sebagai poros.
    if (!water) return;
    const pivot = drag === "top" ? water.b : water.a;
    onArchie(dragEnd(archie, logPhiAtY(pivot.y), cursor));
  };

  const handle = (kind: DragKind, point: { x: number; y: number }) => (
    <ellipse
      key={kind}
      cx={point.x}
      cy={point.y}
      rx={grip.rx}
      ry={grip.ry}
      fill="var(--accent)"
      stroke="var(--panel)"
      strokeWidth={1}
      vectorEffect="non-scaling-stroke"
      style={{ cursor: "ns-resize" }}
      onPointerDown={(event) => {
        event.stopPropagation();
        (event.target as Element).setPointerCapture?.(event.pointerId);
        setDrag(kind);
      }}
    />
  );

  return (
    // `flex: none` begitu tingginya ditentukan. Sebagai anak flex yang tumbuh,
    // tinggi yang ditulis di sini diabaikan sama sekali: basisnya nol, dan di
    // dalam kolom yang bergulir tidak ada ruang sisa untuk ditumbuhi.
    <div style={{ display: "flex", gap: 6, flex: height ? "none" : 1, minHeight: 0, height }}>
      <div style={{ display: "flex", alignItems: "center", flex: "none" }}>
        <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-4)", transform: "rotate(-90deg)", whiteSpace: "nowrap" }}>
          {phiLabel} · log
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", gap: 6, flex: 1, minHeight: 0 }}>
      {/* Kolom angka ini harus setinggi kotaknya persis. Ditaruh sebagai
          saudara dari seluruh kolom kanan — kotak *dan* strip angka sumbu
          mendatar di bawahnya — tiap angka tegak akan turun sejauh tinggi
          strip itu, dan tanda 0,01 mendarat di bawah dasar kotaknya. */}
      <div style={{ width: 34, flex: "none", position: "relative" }}>
        {axes.y.ticks.map((value) => (
          <span
            key={value}
            style={{
              position: "absolute",
              right: 0,
              top: `${PAD + (1 - at(axes.y, value)) * SPAN}%`,
              transform: "translateY(-50%)",
              font: "400 9px var(--font-mono)",
              color: "var(--ink-5)",
              whiteSpace: "nowrap",
            }}
          >
            {value.toFixed(tickDecimals(Math.abs(value) || 1))}
          </span>
        ))}
      </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          ref={(element) => {
            box.current = element;
            sizeRef(element);
          }}
          onPointerMove={onMove}
          onPointerUp={() => setDrag(null)}
          onPointerLeave={() => setDrag(null)}
          style={{
            position: "relative",
            flex: 1,
            minHeight: 210,
            background: "var(--canvas-deep)",
            border: "1px solid var(--line-strong)",
            borderRadius: "var(--radius-panel)",
            overflow: "hidden",
            boxShadow: "var(--shadow-panel)",
            touchAction: "none",
            cursor: drag ? "grabbing" : "default",
          }}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            aria-label={`Pickett ${phiLabel} against ${rtLabel}`}
          >
            {axes.x.ticks.map((value) => (
              <line key={`gx${value}`} x1={PAD + at(axes.x, value) * SPAN} x2={PAD + at(axes.x, value) * SPAN}
                    y1={0} y2={100} stroke="var(--grid-line-strong)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            ))}
            {axes.y.ticks.map((value) => (
              <line key={`gy${value}`} x1={0} x2={100}
                    y1={PAD + (1 - at(axes.y, value)) * SPAN} y2={PAD + (1 - at(axes.y, value)) * SPAN}
                    stroke="var(--grid-line-strong)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            ))}
            {points.map((point, index) => (
              <ellipse key={index} cx={point.cx} cy={point.cy} rx={dot.rx} ry={dot.ry}
                       fill={point.fill} fillOpacity={0.75} style={{ pointerEvents: "none" }} />
            ))}
            {showFit && fit && (
              <path d={fit.d} stroke="var(--amber)" strokeWidth={1.2} strokeDasharray="5 3"
                    fill="none" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
            )}
            {swLines
              .filter((line) => line.sw !== 1)
              .map((line) => (
                <path key={line.sw} d={line.d} stroke="var(--accent)" strokeWidth={0.9}
                      strokeOpacity={0.45} strokeDasharray="3 2" fill="none"
                      vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              ))}
            {water && (
              <>
                <path d={water.d} stroke="var(--accent-bright)" strokeWidth={1.6} fill="none"
                      vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                {/* Sasaran seret: garis tebal tak terlihat, sebab garis setebal
                    satu piksel tidak bisa dikenai kursor. Ia digambar *sesudah*
                    garis yang terlihat, dan bukan sebelumnya: yang terakhir
                    digambar adalah yang teratas, dan garis terlihat yang duduk
                    di atas sasarannya menelan tiap penekanan yang tepat
                    mengenai garisnya — yaitu satu-satunya tempat orang
                    menekan. */}
                <path
                  d={water.d}
                  stroke="transparent"
                  strokeWidth={12}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  style={{ cursor: drag ? "grabbing" : "grab" }}
                  onPointerDown={(event) => {
                    (event.target as Element).setPointerCapture?.(event.pointerId);
                    setDrag("body");
                  }}
                />
                {handle("top", water.a)}
                {handle("bottom", water.b)}
              </>
            )}
          </svg>

          {swLines.map((line) => {
            const spot = labelSpot(line.a);
            return (
              <span
                key={`t${line.sw}`}
                style={{
                  position: "absolute",
                  left: `${spot.left}%`,
                  top: `${spot.top}%`,
                  marginTop: (SW_LINES.indexOf(line.sw) % 2) * 11,
                  // Dibalik ke kiri titiknya di tepi kanan: yang bergeser
                  // adalah tulisannya, bukan garis yang ditunjuknya.
                  transform: spot.flip
                    ? "translate(calc(-100% - 2px), -2px)"
                    : "translate(2px, -2px)",
                  whiteSpace: "nowrap",
                  font: "400 9px var(--font-mono)",
                  color: line.sw === 1 ? "var(--accent-bright)" : "var(--ink-5)",
                  pointerEvents: "none",
                }}
              >
                {line.sw === 1 ? "Sw 1.0" : line.sw.toFixed(1)}
              </span>
            );
          })}

          {/* Kiri bawah, bukan kiri atas: sudut kiri atas adalah tempat label
              garis Sw yang paling basah berdiri, dan keterangan yang menutupi
              garis air adalah keterangan yang menutupi satu-satunya garis yang
              sedang dicocokkan orang. */}
          {/* Menembus penekanan. Kotak ini duduk di atas kanvas, dan kanvas
              di bawahnya adalah tempat garis dicocokkan: sebuah pegangan yang
              kebetulan berada di belakangnya menjadi tidak bisa diseret sama
              sekali — `elementFromPoint` memulangkan SPAN ini, seretannya diam,
              dan tidak ada satu pun error. Yang mencobanya akan menyimpulkan
              bahwa seretan itu memang tidak ada di kolom sempit, sebab di
              layar penuh seretan yang sama persis mendarat. */}
          <span
            style={{
              position: "absolute",
              left: 8,
              bottom: 8,
              padding: "3px 7px",
              background: "var(--panel)",
              border: "1px solid var(--line-strong)",
              borderRadius: 5,
              font: "400 9.5px var(--font-mono)",
              color: "var(--accent-bright)",
              pointerEvents: "none",
            }}
          >
            Rw {archie.rw.toFixed(4)} · m {archie.m.toFixed(2)} · n {archie.n.toFixed(2)}
          </span>

          {showFit && fit && (
            <span
              title={
                fit.refusal
                  ? "This regression is drawn, but not named m and Rw: "
                    + `${fit.refusal}. Fit the line by hand.`
                  : undefined
              }
              style={{
                position: "absolute",
                right: 8,
                bottom: 8,
                padding: "3px 7px",
                background: "var(--panel)",
                border: "1px solid var(--line-strong)",
                borderRadius: 5,
                font: "400 9.5px var(--font-mono)",
                // Ditolak berarti diam: angka yang tidak boleh dipakai tidak
                // boleh pula memakai warna yang dipakai angka yang boleh.
                color: fit.refusal ? "var(--ink-5)" : "var(--amber)",
                display: "flex",
                alignItems: "center",
                gap: 7,
                // Sama seperti kotak di seberangnya: keterangan tidak boleh
                // menelan penekanan yang ditujukan ke garis di bawahnya.
                // Tombolnya sendiri dikecualikan — ia memang untuk ditekan.
                pointerEvents: "none",
              }}
            >
              {fit.refusal ? (
                // Tanpa R² yang berdiri sendiri: tiga dari empat alasan sudah
                // menyebut angkanya masing-masing, dan "R² 0.001 · R² 0.001 —
                // sebaran" adalah satu angka yang dikatakan dua kali.
                <>regression · {fit.refusal}</>
              ) : (
                <>
                  regression · m {fit.read!.m.toFixed(2)} · Rw {fit.read!.rw.toFixed(4)} · R²{" "}
                  {fit.r2 === null ? "—" : fit.r2.toFixed(3)}
                  <button
                    type="button"
                    onClick={() =>
                      onArchie({
                        ...archie,
                        m: clamp(fit.read!.m, M_RANGE),
                        rw: clamp(fit.read!.rw, RW_RANGE),
                      })
                    }
                    style={{
                      background: "none",
                      border: "1px solid var(--line-strong)",
                      borderRadius: 4,
                      padding: "1px 5px",
                      font: "500 9px var(--font-sans)",
                      color: "var(--ink-3)",
                      cursor: "pointer",
                      pointerEvents: "auto",
                    }}
                  >
                    pakai
                  </button>
                </>
              )}
            </span>
          )}
        </div>
      </div>
      </div>
        <div style={{ position: "relative", height: 12, flex: "none", marginLeft: 40 }}>
          {axes.x.ticks.map((value) => (
            <span
              key={value}
              style={{
                position: "absolute",
                left: `${PAD + at(axes.x, value) * SPAN}%`,
                transform: "translateX(-50%)",
                font: "400 9px var(--font-mono)",
                color: "var(--ink-5)",
                whiteSpace: "nowrap",
              }}
            >
              {value.toFixed(tickDecimals(Math.abs(value) || 1))}
            </span>
          ))}
        </div>
        <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-4)", textAlign: "center", flex: "none", marginLeft: 40 }}>
          {rtLabel} · log
        </span>
      </div>
    </div>
  );
}
